import { invisiblyApi } from "src/redux/api/invisibly-api";
import { Client, ClientAccessList, ClientAccessRequest, ClientDashboardAccess, ClientUser, CreateClientRequest } from "../types/client.types";
import { InvisiblyApiResponse } from "src/types/apiResponses";
import { APITagType } from "src/redux/api/invisibly-api.types";
import { ClientSurveyAccess } from "../types/client-management.types";

const clientEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganizationClients: builder.query<Client[], void>({
      query: () => ({
        method: 'get',
        url: 'client',
      }),
      transformResponse(response: InvisiblyApiResponse<Client[]>) {
        return response.item;
      },
      providesTags: [{
        id: 'LIST',
        type: APITagType.OrganizationClients,
      }],
    }),
    getClient: builder.query<Client, string>({
      query: (clientId) => ({
        method: 'get',
        url: `client/${clientId}`,
      }),
      transformResponse(response: InvisiblyApiResponse<Client>) {
        return response.item;
      },
      providesTags: (result, error, clientId) => [{
        id: clientId,
        type: APITagType.OrganizationClients,
      }],
    }),
    createClient: builder.mutation<Client, CreateClientRequest>({
      query: (data) => ({
        data,
        url: 'client',
        method: 'post',
      }),
      transformResponse(response: InvisiblyApiResponse<Client>) {
        return response.item;
      },
      invalidatesTags: [{
        id: 'LIST',
        type: APITagType.OrganizationClients,
      }],
    }),
    createClientUser: builder.mutation<ClientUser, { clientId: string; email: string; }>({
      query: ({ clientId, email }) => ({
        method: 'POST',
        url: `client/user`,
        data: {
          client: clientId,
          email,
        },
      }),
    }),
    resendClientUserInvite: builder.mutation<ClientUser, { clientUserId: string; }>({
      query: ({ clientUserId }) => ({
        method: 'POST',
        url: `client/user/${clientUserId}/invites/resend`,
      }),
    }),
    updateClient: builder.mutation<Client, { clientId: string; data: Partial<Client> }>({
      query: ({ clientId, data }) => ({
        method: 'PATCH',
        url: `client/${clientId}`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<Client>) {
        return response.item;
      },
      async onQueryStarted({ clientId, data }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedClient } = await queryFulfilled;
          dispatch(
            clientEndpoints.util.updateQueryData('getClient', clientId, (draft) => {
              Object.assign(draft, updatedClient);
            })
          );
          dispatch(
            clientEndpoints.util.invalidateTags([{
              id: 'LIST',
              type: APITagType.OrganizationClients,
            }])
          );
        } catch (error) {
          console.error('Update client error:', error);
        }
      },
    }),
    getClientAccountAccess: builder.query<ClientAccessList[], string>({
      query: (clientId) => ({
        method: 'get',
        url: `client/${clientId}/access`,
      }),
      transformResponse(response: InvisiblyApiResponse<ClientAccessList[]>) {
        return response.item;
      },
      keepUnusedDataFor: 0,
    }),
    updateClientAccountAccess: builder.mutation<void, ClientAccessRequest[]>({
      query: (data) => ({
        method: 'post',
        url: 'client/access',
        data,
      }),
      invalidatesTags: (result, error, data) => {
        // Invalidate the client list
        const tags: Array<{ id: string | 'LIST'; type: APITagType }> = [{
          id: 'LIST',
          type: APITagType.OrganizationClients,
        }];

        // Also invalidate the specific client if we have the client ID
        if (data && data.length > 0 && data[0].ext_client_id) {
          tags.push({
            id: data[0].ext_client_id,
            type: APITagType.OrganizationClients,
          });
          tags.push({
            id: data[0].ext_client_id,
            type: APITagType.ClientCampaignAccess,
          });
        }

        return tags;
      },
    }),
    deleteClientAccountAccess: builder.mutation<void, { ext_client_id: string; }>({
      query: (data) => ({
        method: 'delete',
        url: `client/access/${data.ext_client_id}/delete`,
      }),
      invalidatesTags: (_result, _error, { ext_client_id }) => [
        { id: 'LIST', type: APITagType.OrganizationClients },
        { id: ext_client_id, type: APITagType.OrganizationClients },
        { id: ext_client_id, type: APITagType.ClientCampaignAccess },
      ],
    }),

    createClientSurveyAccess: builder.mutation<void, { client_id: string; ext_survey_id: string; }>({
      query: (data) => ({
        method: 'post',
        url: `b2b/survey/${data.ext_survey_id}/client`,
        data,
      }),
    }),
    getClientSurveyAccess: builder.query<ClientSurveyAccess[], { ext_survey_id: string; }>({
      query: (data) => ({
        method: 'get',
        url: `b2b/survey/${data.ext_survey_id}/clients`,
      }),
      transformResponse(response: InvisiblyApiResponse<ClientSurveyAccess[]>) {
        return response.item;
      },
    }),
    deleteClientSurveyAccess: builder.mutation<void, { client_id: string; ext_survey_id: string; }>({
      query: (data) => ({
        method: 'delete',
        url: `b2b/survey/${data.ext_survey_id}/client`,
        data,
      }),
    }),

    createClientDashboardAccess: builder.mutation<void, ClientDashboardAccess[]>({
      query: (data) => ({
        method: 'post',
        url: `client/dashboard/access`,
        data,
      }),
      invalidatesTags: (result, error, data) => {
        // Invalidate the client list
        const tags: Array<{ id: string | 'LIST'; type: APITagType.OrganizationClients }> = [{
          id: 'LIST',
          type: APITagType.OrganizationClients,
        }];

        // Also invalidate the specific client if we have the client ID
        if (data && data.length > 0 && data[0].client) {
          tags.push({
            id: data[0].client,
            type: APITagType.OrganizationClients,
          });
        }

        return tags;
      },
    }),
    deleteClientDashboardAccess: builder.mutation<void, { dashboard_access_id: string; }>({
      query: ({ dashboard_access_id }) => ({
        method: 'delete',
        url: `client/dashboard/access/${dashboard_access_id}`,
      }),
    }),

    deleteClientUser: builder.mutation<void, { clientUserId: string }>({
      query: ({ clientUserId }) => ({
        method: 'delete',
        url: `client/user/${clientUserId}`,
      }),
    }),
    deleteClient: builder.mutation<void, { clientId: string }>({
      query: ({ clientId }) => ({
        method: 'delete',
        url: `client/${clientId}`,
      }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { id: 'LIST', type: APITagType.OrganizationClients },
        { id: clientId, type: APITagType.OrganizationClients },
      ],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetOrganizationClientsQuery,
  useGetClientQuery,
  useCreateClientMutation,
  useUpdateClientMutation,

  useGetClientAccountAccessQuery,
  useUpdateClientAccountAccessMutation,
  useDeleteClientAccountAccessMutation,

  useCreateClientUserMutation,
  useResendClientUserInviteMutation,
  useDeleteClientUserMutation,

  useCreateClientSurveyAccessMutation,
  useGetClientSurveyAccessQuery,
  useLazyGetClientSurveyAccessQuery,
  useDeleteClientSurveyAccessMutation,

  useCreateClientDashboardAccessMutation,
  useDeleteClientDashboardAccessMutation,
  useDeleteClientMutation,
} = clientEndpoints;